import { Suspense, lazy, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { motion } from "framer-motion";
// L18 bundle-split: the hot path (LoginRoute + ChatRoute + their error
// boundaries) stays eagerly imported so the first paint after sign-in
// doesn't pay a fetch round-trip. Everything else lazy-loads into its
// own named chunk — see lazy() calls below. The Suspense fallback
// renders a minimal loading state per route while the chunk arrives.
import { LoginRoute, LOGIN_SCREENS } from "./routes/LoginRoute";
import type { LoginScreen } from "./routes/LoginRoute";
import { ChatRoute } from "./routes/ChatRoute";
import { IndianErrorBoundary } from "./features/experimental/indian/ErrorBoundary";
import { JarvisErrorBoundary } from "./features/experimental/jarvis/ErrorBoundary";
import { ExperimentalErrorBoundary } from "./features/experimental/ErrorBoundary";
import { CookieConsent } from "./components/CookieConsent";
import { PasswordBackfillGate } from "./components/PasswordBackfillGate";

// Lazy chunks — Vite emits one chunk per dynamic import. Named exports
// are re-mapped to `default` via the lazy wrapper so React.lazy is happy.
const FeaturesScreen = lazy(() => import("./screens/features/FeaturesScreen").then(m => ({ default: m.FeaturesScreen })));
const FeatureDetailScreen = lazy(() => import("./screens/features/FeatureDetailScreen").then(m => ({ default: m.FeatureDetailScreen })));
const OcrRoute = lazy(() => import("./routes/OcrRoute").then(m => ({ default: m.OcrRoute })));
const OcrProRoute = lazy(() => import("./routes/OcrProRoute").then(m => ({ default: m.OcrProRoute })));
const AgentScreen = lazy(() => import("./screens/agent/AgentScreen").then(m => ({ default: m.AgentScreen })));
const MyPoliciesScreen = lazy(() => import("./screens/policies/MyPoliciesScreen").then(m => ({ default: m.MyPoliciesScreen })));
const FamilyScreen = lazy(() => import("./screens/family/FamilyScreen").then(m => ({ default: m.FamilyScreen })));
const ConsoleScreen = lazy(() => import("./screens/console/ConsoleScreen").then(m => ({ default: m.ConsoleScreen })));
const AdminDashboardScreen = lazy(() => import("./screens/admin/AdminDashboardScreen").then(m => ({ default: m.AdminDashboardScreen })));
const TrainingReviewScreen = lazy(() => import("./screens/admin/TrainingReviewScreen").then(m => ({ default: m.TrainingReviewScreen })));
const AdminShell = lazy(() => import("./screens/admin/AdminShell").then(m => ({ default: m.AdminShell })));
const PricingScreen = lazy(() => import("./screens/plans/PricingScreen").then(m => ({ default: m.PricingScreen }))); // BE: React monetization port
const AgentDashboardScreen = lazy(() => import("./screens/book/AgentDashboardScreen").then(m => ({ default: m.AgentDashboardScreen })));
const AgencyDashboardScreen = lazy(() => import("./screens/agency/AgencyDashboardScreen").then(m => ({ default: m.AgencyDashboardScreen })));
const ProposalScreen = lazy(() => import("./screens/booking/ProposalScreen").then(m => ({ default: m.ProposalScreen })));
const ClaimsScreen = lazy(() => import("./screens/claims/ClaimsScreen").then(m => ({ default: m.ClaimsScreen })));
const ClaimDetailScreen = lazy(() => import("./screens/claims/ClaimDetailScreen").then(m => ({ default: m.ClaimDetailScreen })));
const AgentClaimsQueue = lazy(() => import("./screens/claims/AgentClaimsQueue").then(m => ({ default: m.AgentClaimsQueue })));
const BotReviewQueueScreen = lazy(() => import("./screens/console/BotReviewQueueScreen").then(m => ({ default: m.BotReviewQueueScreen })));
const FlywheelDashboard = lazy(() => import("./screens/console/FlywheelDashboard").then(m => ({ default: m.FlywheelDashboard })));
const CapabilityBoard = lazy(() => import("./screens/console/CapabilityBoard").then(m => ({ default: m.CapabilityBoard })));
const OcrReviewQueueScreen = lazy(() => import("./screens/console/OcrReviewQueueScreen").then(m => ({ default: m.OcrReviewQueueScreen })));
const CommunityScreen = lazy(() => import("./screens/community/CommunityScreen").then(m => ({ default: m.CommunityScreen })));
const LearnScreen = lazy(() => import("./screens/learn/LearnScreen").then(m => ({ default: m.LearnScreen })));
const RecommendationsScreen = lazy(() => import("./screens/recommendations/RecommendationsScreen").then(m => ({ default: m.RecommendationsScreen })));
const SettingsScreen = lazy(() => import("./screens/settings/SettingsScreen").then(m => ({ default: m.SettingsScreen })));
const VoiceScreen = lazy(() => import("./screens/voice/VoiceScreen").then(m => ({ default: m.VoiceScreen })));
const SelfTestScreen = lazy(() => import("./selftest/SelfTestScreen").then(m => ({ default: m.SelfTestScreen })));
const IndianToolScreen = lazy(() => import("./features/experimental/indian/IndianToolScreen").then(m => ({ default: m.IndianToolScreen })));
const JarvisHubScreen = lazy(() => import("./features/experimental/jarvis/JarvisHubScreen").then(m => ({ default: m.JarvisHubScreen })));
const ExperimentalHubScreen = lazy(() => import("./features/experimental/ExperimentalHubScreen").then(m => ({ default: m.ExperimentalHubScreen })));
const ExperimentDetailScreen = lazy(() => import("./features/experimental/ExperimentDetailScreen").then(m => ({ default: m.ExperimentDetailScreen })));

// Minimal Suspense fallback — matches the app shell so route swaps
// don't flash a blank screen. Honest text rather than a spinner so
// users on slow connections see what's happening.
function RouteFallback() {
  return (
    <div
      className="min-h-[100dvh] w-full grid place-items-center bg-[var(--color-paper)] text-[var(--color-ink)]"
      role="status"
      aria-live="polite"
    >
      <p className="text-[13px] text-[var(--color-ink-60)]">Loading screen…</p>
    </div>
  );
}

/**
 * Beyond Sure — Launch 2 UI sandbox entry (PHASE 0 routing overhaul).
 *
 * Routes (real client-side, no more `?screen=` hack):
 *   • `/`      → reserved for the public marketing landing.
 *                Landing isn't built yet — for now we redirect to /login.
 *                TODO: replace with the real landing page (separate build).
 *   • `/login` → the locked auth + onboarding wizard. Owns its own
 *                state machine inside LoginRoute (signin → otp → success
 *                → onboarding → intro). Completion navigates to /chat.
 *   • `/chat`  → the main chatbot surface (placeholder in Phase 0,
 *                real shell lands in Phase 1).
 *
 * Mock backend (independent — nothing real wired yet):
 *   • TODO: POST /api/otp/send             (in SignInScreen)
 *   • TODO: POST /api/otp/verify           (in OtpScreen)
 *   • TODO: POST /api/user/onboarding/save (in OnboardingFlow)
 *   • TODO: GET  /api/user/me               (to pull the verified user)
 *   • TODO: POST /api/upload/insurance     (in OnboardingFlow step 9)
 *   • TODO: POST /api/chat/message         (streaming chat replies)
 *   • TODO: POST /api/chat/upload          (OCR document in chat)
 *   • TODO: GET  /api/chat/history          (sidebar conversation list)
 *
 * Vite handles SPA history fallback in dev. In production the Flask
 * catch-all (or an Nginx fallback) needs to serve index.html for any
 * non-/api/, non-static path.
 * TODO: prod SPA catch-all (serve index.html for /login, /chat, /).
 */

function App() {
  return (
    // basename = Vite base ("/", BF3 fix 2026-07-15 — prod nginx serves this build at the
    // domain root, not under /app/) so routes resolve against the actual served path. Routes
    // like "/admin" match plain "/admin"; a stale redirect to "/app/admin" from before this
    // fix falls through the "*" → /login catch-all instead. React Router strips the trailing
    // slash internally.
    <BrowserRouter basename={import.meta.env.BASE_URL}>
      {/* BD1.4 — forced password backfill for passwordless accounts, on any
          authed route (catches Google-OAuth returns + stale sessions that
          never pass through the /login flow). Skips /login and /admin*. */}
      <PasswordBackfillGate />
      {/* Suspense boundary for L18 bundle-split lazy routes. The
          fallback renders only while a chunk is fetching — instant
          for cached chunks, brief for first visit. */}
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public marketing landing — not built yet. */}
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* Auth + onboarding wizard. */}
        <Route path="/login" element={<LoginRouteEntry />} />

        {/* Main chatbot surface. */}
        <Route path="/chat" element={<ChatRoute />} />

        {/* Features catalog + the single reusable detail template. */}
        <Route path="/features" element={<FeaturesScreen />} />
        <Route path="/features/:slug" element={<FeatureDetailScreen />} />

        {/* Universal OCR document page (upload + auto-analysis + Ask AI). */}
        <Route path="/documents" element={<OcrRoute />} />

        {/* Dedicated UNIVERSAL OCR product — Fracto competitor at
            ~1/10 cost. Works on ANY document, multi-model engine,
            comparison + benchmark + doc Ask-AI. */}
        <Route path="/ocr" element={<OcrProRoute />} />

        {/* PART B — Agent experience (preview). Plan → approve →
            live progress → confirm-before-sensitive → audit log.
            Execution is simulated client-side; the live runtime
            (Skyvern / Browser Use / GSP API) is in PART C TODOs. */}
        <Route path="/agent" element={<AgentScreen />} />

        {/* Unified app screens — all mount the same UnifiedSidebar via AppShell.
            The legacy form.html versions still exist in Flask-land but the
            React app now owns the canonical sidebar experience. */}
        <Route path="/policies" element={<MyPoliciesScreen />} />
        <Route path="/family" element={<FamilyScreen />} />
        {/* BE — React monetization port: plans catalog + Razorpay checkout. */}
        <Route path="/plans" element={<PricingScreen />} />

        {/* Admin Console v2 — guarded React area, separate from admin.html. */}
        <Route path="/console" element={<ConsoleScreen />} />

        {/* L09a — Deepak's bot review queue (priority + cluster view +
            expert answer editor + WHY field + approve-as-KB toggle).
            Closes the C1 cloning loop into real DPO/SFT pairs. */}
        <Route path="/console/bot-review" element={<BotReviewQueueScreen />} />

        {/* L09b — Flywheel dashboard: SFT/DPO pair counts, fine-tune gate
            progress with the server's verbatim reason, adapter lifecycle
            (honest empty state until QLoRA gate opens), trainer
            leaderboard from ai_corrections in the last 7 days. */}
        <Route path="/console/flywheel" element={<FlywheelDashboard />} />

        {/* L09c — C5 capability board: every Beyond Sure capability with
            REAL-PROVEN vs GATED status (verbatim from server), the gate
            slug for what's missing, and path:line evidence. */}
        <Route path="/console/capabilities" element={<CapabilityBoard />} />

        {/* L09d — OCR review queue: recent OCR failures + accuracy feedback
            with by-doc-type breakdown. Operator-facing visibility into
            where the pipeline needs attention. */}
        <Route path="/console/ocr-review" element={<OcrReviewQueueScreen />} />

        {/* L10a — Community Q&A: posts list + create + detail + answers +
            voting. Covers 5 endpoints under /api/community/* — closes the
            biggest UI-PARITY MISSING gap after Claims (already shipped). */}
        <Route path="/community" element={<CommunityScreen />} />

        {/* L10b — Learn / Education: 3 tabs (Myth debunker + Insurance IQ
            quiz + Tricky terms glossary). Pure static curated content
            per spec ("mostly static"); every claim cites IRDAI/Insurance
            Act source. */}
        <Route path="/learn" element={<LearnScreen />} />

        {/* Admin Org Dashboard (R4/L-2) — real hierarchy APIs: View A KPIs +
            searchable employee roster (/api/admin/overview + /employees). */}
        <Route path="/admin-dashboard" element={<AdminDashboardScreen />} />

        {/* Training Review (Deepak founder loop) — React mirror of the admin.html tab;
            cookie-auth /admin/api/training/review/*. Ready for the React-admin cutover. */}
        <Route path="/admin-training-review" element={<TrainingReviewScreen />} />

        {/* React-admin SHELL (Phase-0 cutover) — sidebar + topbar hosting every admin tab
            under one ig_admin_token cookie login. /admin-react is always reachable in the
            React app; /admin renders it too when the server serves React (ADMIN_REACT_UI=1). */}
        <Route path="/admin-react" element={<AdminShell />} />
        <Route path="/admin" element={<AdminShell />} />

        {/* Agent dashboard — book of business (advisor-facing). */}
        <Route path="/book" element={<AgentDashboardScreen />} />

        {/* Agency dashboard — the top tier (agency → agent → client → family).
            Reuses AgentDashboardView for the per-agent drill, which nests the
            customer Family hub's CoverageMatrix + GapsList. One graph, four views. */}
        <Route path="/agency" element={<AgencyDashboardScreen />} />

        {/* L08 — Transact UI: booking flow. /proposal/:bookingId mounts
            ProposalScreen which drives the entire intent → proposal → kyc →
            payment_pending → issued state machine, including the honest
            compliance-gated PaymentHandoffStep (per L08 spec). */}
        <Route path="/proposal/:bookingId" element={<ProposalScreen />} />

        {/* L08 — Transact UI: claims dashboard. /claims surfaces the
            two-dimension status model (advisory + filing) honestly so
            users always know exactly where their claim is. */}
        <Route path="/claims" element={<ClaimsScreen />} />

        {/* L08-cont (b): advisor cross-customer queue. MUST come before
            /claims/:claimId so "queue" isn't matched as a claim id. */}
        <Route path="/claims/queue" element={<AgentClaimsQueue />} />

        {/* L08-cont (a): per-claim deep-dive with M3 authorize + file
            steps. SubmissionGatedCard is the claims sibling of
            PaymentHandoffStep — same compile-time-enforced honest gate. */}
        <Route path="/claims/:claimId" element={<ClaimDetailScreen />} />

        {/* Recommendation engine — prioritised, ₹-quantified next-best-actions
            derived from the shared household graph (v2-M6). */}
        <Route path="/recommendations" element={<RecommendationsScreen />} />

        {/* Settings · Data & privacy — the customer-facing training-consent
            toggle (calls /api/consent/training). Defense always on; offense opt-in. */}
        <Route path="/settings" element={<SettingsScreen />} />

        {/* Voice console — browser STT + /api/tts read-back (v2-M4). */}
        <Route path="/voice" element={<VoiceScreen />} />

        {/* Experimental Tools — enterprise-only "Indian" tab (isolated module under
            features/experimental/indian/; server-gated via /api/experimental/indian/*;
            error-bounded so a tool failure never breaks the app shell). */}
        <Route
          path="/indian"
          element={
            <IndianErrorBoundary>
              <IndianToolScreen />
            </IndianErrorBoundary>
          }
        />

        {/* JARVIS — enterprise-only domain mastery hub (L17). Isolated module
            under features/experimental/jarvis/; server-gated via
            /api/experimental/jarvis/*; error-bounded so a tool failure never
            breaks the app shell. */}
        <Route
          path="/jarvis"
          element={
            <JarvisErrorBoundary>
              <JarvisHubScreen />
            </JarvisErrorBoundary>
          }
        />

        {/* Experimental TAB — enterprise-only sandbox hub (R0). Card grid of
            competitor-inspired tools → per-tool detail page (inspiration · analysis ·
            market gap · what-we-built · sources), driven by the manifest in
            features/experimental/registry.ts. Shell-level error boundary keeps a tool
            failure from ever reaching the core app. Each tool's LIVE endpoints stay
            server-gated behind EXPERIMENTAL_TOOLS_ENABLED. */}
        <Route
          path="/experimental"
          element={
            <ExperimentalErrorBoundary>
              <ExperimentalHubScreen />
            </ExperimentalErrorBoundary>
          }
        />
        <Route
          path="/experimental/:id"
          element={
            <ExperimentalErrorBoundary>
              <ExperimentDetailScreen />
            </ExperimentalErrorBoundary>
          }
        />

        {/* Derivation self-tests (CI-verified unit surface). */}
        <Route path="/selftest" element={<SelfTestScreen />} />

        {/* Anything else → /login for now. Replace once the landing exists. */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      </Suspense>

      <CookieConsent />

      {/* Dev-only floating route switcher — tree-shaken from prod. */}
      {import.meta.env.DEV ? <DevRouteSwitcher /> : null}
    </BrowserRouter>
  );
}

/**
 * Wrapper around LoginRoute that reads `?screen=...` and `?email=...` on
 * mount and primes the internal state. Lets the dev switcher jump straight
 * into onboarding step 5 without clicking through OTP each time. The query
 * params are NOT used in production — `import.meta.env.DEV` gates the
 * propagation.
 */
function LoginRouteEntry() {
  const location = useLocation();
  let initialScreen: LoginScreen | undefined;
  let initialEmail: string | undefined;
  if (import.meta.env.DEV) {
    const params = new URLSearchParams(location.search);
    const screen = params.get("screen");
    if (screen && (LOGIN_SCREENS as string[]).includes(screen)) {
      initialScreen = screen as LoginScreen;
    }
    const email = params.get("email");
    if (email) initialEmail = email;
  }
  return <LoginRoute initialScreen={initialScreen} initialEmail={initialEmail} />;
}

/**
 * Floating dev pill — top-right corner, only mounted under `vite dev`.
 * Lets the founder jump to any route (and any /login sub-screen) without
 * walking through the flow.
 */
function DevRouteSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const items: { label: string; to: string; group: "route" | "login" }[] = [
    { label: "/login", to: "/login", group: "route" },
    { label: "/chat", to: "/chat", group: "route" },
    { label: "/features", to: "/features", group: "route" },
    { label: "/documents", to: "/documents", group: "route" },
    { label: "/ocr", to: "/ocr", group: "route" },
    { label: "/agent", to: "/agent", group: "route" },
    { label: "/policies", to: "/policies", group: "route" },
    { label: "/family", to: "/family", group: "route" },
    { label: "/console", to: "/console", group: "route" },
    { label: "/book", to: "/book", group: "route" },
    { label: "/agency", to: "/agency", group: "route" },
    { label: "/recommendations", to: "/recommendations", group: "route" },
    { label: "/settings", to: "/settings", group: "route" },
    { label: "/voice", to: "/voice", group: "route" },
    { label: "/indian (enterprise)", to: "/indian", group: "route" },
    { label: "/experimental (enterprise)", to: "/experimental", group: "route" },
    { label: "/ (landing — TODO)", to: "/", group: "route" },
    ...LOGIN_SCREENS.map((s) => ({
      label: `/login ?screen=${s}`,
      to: `/login?screen=${s}`,
      group: "login" as const,
    })),
  ];

  // Display label: route or "/login ?screen=onboarding"
  const params = new URLSearchParams(location.search);
  const screenParam = params.get("screen");
  const current =
    location.pathname === "/login" && screenParam
      ? `/login ?screen=${screenParam}`
      : location.pathname;

  return (
    <div className="fixed top-3 right-3 z-[80] flex flex-col items-end gap-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={[
          "bsure-ui text-[11px] font-semibold uppercase tracking-[0.16em]",
          "rounded-full px-3 py-1.5 backdrop-blur",
          "bg-[rgb(0_0_0_/_0.7)] text-[var(--color-paper)]",
          "hover:bg-[rgb(0_0_0_/_0.85)] transition-colors",
        ].join(" ")}
        aria-expanded={open}
        aria-controls="dev-routes"
      >
        Dev · {current}
      </button>
      {open ? (
        <motion.div
          id="dev-routes"
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col gap-1 rounded-[var(--radius-md)] bg-[rgb(0_0_0_/_0.85)] text-[var(--color-paper)] p-1.5 backdrop-blur max-h-[80vh] overflow-y-auto"
        >
          {items.map((item) => (
            <button
              key={item.to}
              type="button"
              onClick={() => {
                navigate(item.to);
                setOpen(false);
              }}
              className={[
                "bsure-ui text-left text-[11.5px] rounded-[var(--radius-sm)] px-3 py-1.5",
                item.to === location.pathname + location.search ||
                item.to === location.pathname
                  ? "bg-[var(--color-brand-deep)] text-[var(--color-paper)]"
                  : "hover:bg-[rgb(255_255_255_/_0.1)]",
                item.group === "login" ? "opacity-80" : "",
              ].join(" ")}
            >
              {item.label}
            </button>
          ))}
        </motion.div>
      ) : null}
    </div>
  );
}

export default App;
